import React, { useState, useMemo, useEffect, useRef } from 'react';
import ReactMarkdown from 'react-markdown';
import 'bootstrap/dist/css/bootstrap.min.css';
import 'bootstrap/dist/js/bootstrap.bundle.min.js';
import './App.css';
import { events } from './events';
import { dailyWeather, fToC } from './weatherDaily';
import { getRecipeForDate, generateShoppingList } from './mealData';
import { getStoryForDate } from './storyEngine';
import { getIndicatorData } from './tradingData';
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from 'recharts';

// --- SELF-CONTAINED SAINTS DATA ---
import saintsDataRaw from './content/catholic_saints_full_2026.json';
import lectionaryData from './content/lectionary_2026.json';
import bibleTexts from './content/bible_texts.json';

const App = () => {
  // --- DATA SAFETY CHECKS ---
  const saintsData = Array.isArray(saintsDataRaw) ? saintsDataRaw : [];
  const lectionaryDataArray = Array.isArray(lectionaryData) ? lectionaryData : [];

  const [currentDate, setCurrentDate] = useState(new Date());
  const [showSidebar, setShowSidebar] = useState(() => {
    const saved = localStorage.getItem('calendar_sidebar_open');
    return saved !== null ? JSON.parse(saved) : true;
  });
  
  const [selectedRecipe, setSelectedRecipe] = useState(null);
  const [recipeMarkdown, setRecipeMarkdown] = useState("");
  const [selectedStory, setSelectedStory] = useState(null);
  const [selectedScript, setSelectedScript] = useState(null);
  const [selectedSaint, setSelectedSaint] = useState(null);
  const [selectedLectionary, setSelectedLectionary] = useState(null);
  const [selectedTrading, setSelectedTrading] = useState(null);
  const [storyContent, setStoryContent] = useState("");
  const [scriptContent, setScriptContent] = useState("");
  const [tradingContent, setTradingContent] = useState("");
  const [costAnalysisContent, setCostAnalysisContent] = useState("");
  const [showShopList, setShowShopList] = useState(false);
  const [activeShopDate, setActiveShopDate] = useState(null);
  const [showSitePlan, setShowSitePlan] = useState(false);
  const [showFrontierPlan, setShowFrontierPlan] = useState(false);
  const [showCostAnalysis, setShowCostAnalysis] = useState(false);
  const [showSoloGuide, setShowSoloGuide] = useState(false);
  const todayRef = useRef(null);

  useEffect(() => {
    if (todayRef.current) {
      todayRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [currentDate]);

  useEffect(() => {
    if (selectedRecipe && selectedRecipe.link) {
      fetch(selectedRecipe.link)
        .then(res => res.text())
        .then(text => setRecipeMarkdown(text))
        .catch(() => setRecipeMarkdown("### 🍳 Improved Recipe Not Found\n\nThe culinary team is still refining this specific protocol."));
    } else {
      setRecipeMarkdown("");
    }
  }, [selectedRecipe]);

  useEffect(() => {
    if (showCostAnalysis) {
      fetch('/docs/castle_cost_analysis.md')
        .then(res => res.text())
        .then(text => setCostAnalysisContent(text))
        .catch(() => setCostAnalysisContent("### 💰 Cost Analysis Not Found\n\nThe documentation is currently being updated."));
    }
  }, [showCostAnalysis]);

  useEffect(() => {
    if (selectedTrading) {
      const url = `/trading/${selectedTrading.date.toISOString().split('T')[0]}-plan.md`;
      fetch(url)
        .then(res => res.text())
        .then(text => setTradingContent(text))
        .catch(() => setTradingContent("### 📈 Trading Plan Not Found\n\nNo specific plan was generated for this day. Refer to the [Master Trading Protocol](/trading/Master_Protocol.md)."));
    }
  }, [selectedTrading]);

  useEffect(() => {
    if (selectedScript) {
      const ds = selectedScript.date.toISOString().split('T')[0];
      const url = `/scripts/${ds}_script.md`;
      fetch(url)
        .then(res => {
          if (!res.ok) throw new Error("Script not found");
          return res.text();
        })
        .then(text => setScriptContent(text))
        .catch(() => setScriptContent("### 📜 Daily Script Not Found\n\nNo detailed script was found for this date."));
    } else {
      setScriptContent("");
    }
  }, [selectedScript]);

  useEffect(() => {
    if (selectedStory) {
      const url = `/stories/${selectedStory.fileName}`;
      console.log("Fetching story from:", url);
      fetch(url)
        .then(res => {
          if (!res.ok) throw new Error("File not found");
          return res.text();
        })
        .then(text => {
          if (text.trim().startsWith("<!DOCTYPE html>")) {
            throw new Error("Received HTML instead of Markdown");
          }
          setStoryContent(text);
        })
        .catch(err => {
          console.error("Story fetch error:", err);
          setStoryContent(`### Story Draft: ${selectedStory.name}\n\n*The technical guide is ready below. The full narrative text for this specific day is being synchronized. Please check your browser console for path details.*`);
        });
    } else {
      setStoryContent("");
    }
  }, [selectedStory]);

  const [filters, setFilters] = useState({
    us: true, indo: true, sports: true, special: true, kansas: true,
    finance: true, gardening: true, market: true, nature: true,
    fun: true, personal: true, meals: true, stories: true
  });

  useEffect(() => {
    localStorage.setItem('calendar_sidebar_open', JSON.stringify(showSidebar));
  }, [showSidebar]);

  const monthsIndo = ["Januari", "Februari", "Maret", "April", "Mei", "Juni", "Juli", "Agustus", "September", "Oktober", "November", "Desember"];
  const daysIndo = ["Senin", "Selasa", "Rabu", "Kamis", "Jumat", "Sabtu", "Minggu"];

  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDayOfMonth = new Date(year, month, 1);
    const lastDayOfMonth = new Date(year, month + 1, 0);
    const days = [];
    let startDay = firstDayOfMonth.getDay(); 
    startDay = (startDay === 0) ? 6 : startDay - 1;
    const prevMonthLastDay = new Date(year, month, 0).getDate();
    for (let i = startDay - 1; i >= 0; i--) days.push({ date: new Date(year, month - 1, prevMonthLastDay - i), isCurrentMonth: false });
    for (let i = 1; i <= lastDayOfMonth.getDate(); i++) days.push({ date: new Date(year, month, i), isCurrentMonth: true });
    const remainingCells = 42 - days.length;
    for (let i = 1; i <= remainingCells; i++) days.push({ date: new Date(year, month + 1, i), isCurrentMonth: false });
    return days;
  };

  const nextMonth = () => setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 1));
  const prevMonth = () => setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1, 1));
  const toggleFilter = (category) => setFilters(prev => ({ ...prev, [category]: !prev[category] }));
  const toggleSidebar = () => setShowSidebar(!showSidebar);

  const getEventsForDate = (date) => {
    // Prevent timezone shift by using local date components
    const ds = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
    const eventsToReturn = [];
    
    // Combine base events with self-contained saints data
    const combinedEvents = [
      ...events,
      ...saintsData
    ];

    const lectionary = lectionaryDataArray.find(l => l.date === ds);
    if (lectionary) {
      const gospelPart = lectionary.gospel ? lectionary.gospel.split(' ')[0] : "Readings";
      eventsToReturn.push({ 
        title: "📖 " + gospelPart, 
        category: "special", 
        lectionaryDetails: lectionary,
        colorOverride: lectionary.identity?.color
      });
    }

    const filtered = combinedEvents.filter(e => e.date === ds && filters[e.category]).map(e => {
      if (e.recipeDetails) {
        return { ...e, recipeDetails: { ...e.recipeDetails, date: new Date(date) } };
      }
      return e;
    });
    
    eventsToReturn.push(...filtered);
    
    if (filters.meals) {
      const startDate = new Date(2026, 0, 5);
      const diffTime = date - startDate;
      const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
      if (diffDays >= 0 && diffDays % 14 === 0) {
        eventsToReturn.push({ title: "🛒 Belanja ALDI (14 Hari)", category: "meals", isShopping: true, shopDate: new Date(date) });
      }
      const recipe = getRecipeForDate(date);
      if (recipe) eventsToReturn.push({ title: `🍳 ${recipe.name}`, category: "meals", recipeDetails: { ...recipe, date: new Date(date) } });
    }

    if (filters.stories) {
      const story = getStoryForDate(date);
      if (story) eventsToReturn.push({ title: `📖 ${story.name}`, category: "stories", storyDetails: { ...story, date: new Date(date) } });
    }

    return eventsToReturn;
  };

  const getReadingLink = (date) => {
    const mm = String(date.getMonth() + 1).padStart(2, '0');
    const dd = String(date.getDate()).padStart(2, '0');
    const yy = String(date.getFullYear()).slice(-2);
    return `https://bible.usccb.org/bible/readings/${mm}${dd}${yy}.cfm`;
  };

  const getFlag = (cuisine) => {
    const flags = { 
      "Cantonese Chinese": "🇨🇳", 
      "Cantonese Modern": "🇨🇳",
      "Japanese Kaiseki": "🇯🇵", 
      "Indonesian West Java": "🇮🇩", 
      "Korean Royal Court": "🇰🇷", 
      "Thai Coastal": "🇹🇭" 
    };
    return flags[cuisine] || "🏳️";
  };

  const days = getDaysInMonth(currentDate);
  const simulatedToday = new Date();

  const chartData = useMemo(() => {
    const monthStr = `2026-${String(currentDate.getMonth() + 1).padStart(2, '0')}`;
    return dailyWeather.filter(w => w.date.startsWith(monthStr)).map(w => ({ day: parseInt(w.date.split('-')[2]), 'High (°F)': w.high, 'Low (°F)': w.low }));
  }, [currentDate]);

  const dynamicShoppingList = useMemo(() => {
    const baseDate = activeShopDate || currentDate;
    return generateShoppingList(baseDate);
  }, [activeShopDate, currentDate]);

  const tradingChartData = useMemo(() => {
    if (!selectedTrading || !selectedTrading.date) return [];
    return getIndicatorData(selectedTrading.date);
  }, [selectedTrading]);

  return (
    <>
      <div className="container-fluid py-4 px-lg-5">
        {/* SCRIPT MODAL */}
      {selectedScript && (
        <div className="modal-overlay" onClick={() => setSelectedScript(null)}>
          <div className="modal-card shadow-lg p-0" style={{ maxWidth: '800px' }} onClick={e => e.stopPropagation()}>
            <div className="modal-header bg-info text-dark p-4">
              <div>
                <span className="badge bg-dark text-white mb-2">📜 365 DAILY INTELLIGENCE SCRIPT</span>
                <h2 className="m-0 fw-bold">{selectedScript.date.toDateString()}</h2>
              </div>
              <button className="btn-close" onClick={() => setSelectedScript(null)}></button>
            </div>
            <div className="modal-body p-4">
              <div className="markdown-content">
                <ReactMarkdown>{scriptContent}</ReactMarkdown>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* LECTIONARY MODAL */}
      {selectedLectionary && (
        <div className="modal-overlay" onClick={() => setSelectedLectionary(null)}>
          <div className="modal-card shadow-lg p-0" style={{ maxWidth: '600px' }} onClick={e => e.stopPropagation()}>
            <div className="modal-header bg-primary text-white p-4">
              <div>
                <span className="badge bg-white text-primary mb-2">📖 LITURGICAL READINGS</span>
                <h2 className="m-0 fw-bold">{selectedLectionary.title}</h2>
              </div>
              <button className="btn-close btn-close-white" onClick={() => setSelectedLectionary(null)}></button>
            </div>
            <div className="modal-body p-4">
              <div className="list-group list-group-flush">
                {/* 1st Reading */}
                {selectedLectionary.firstReading && (
                  <div className="list-group-item px-0">
                    <h6 className="fw-bold text-muted small uppercase mb-1">First Reading</h6>
                    <p className="mb-0 fs-5">{selectedLectionary.firstReading}</p>
                    {bibleTexts[selectedLectionary.firstReading] && (
                      <div className="mt-3 p-3 bg-secondary bg-opacity-10 border-start border-4 border-secondary rounded-end">
                        <p className="mb-0 small leading-relaxed text-dark">{bibleTexts[selectedLectionary.firstReading]}</p>
                      </div>
                    )}
                  </div>
                )}

                {/* 2nd Reading (Optional) */}
                {selectedLectionary.secondReading && (
                  <div className="list-group-item px-0">
                    <h6 className="fw-bold text-muted small uppercase mb-1">Second Reading</h6>
                    <p className="mb-0 fs-5">{selectedLectionary.secondReading}</p>
                    {bibleTexts[selectedLectionary.secondReading] && (
                      <div className="mt-3 p-3 bg-info bg-opacity-10 border-start border-4 border-info rounded-end">
                        <p className="mb-0 small leading-relaxed text-dark">{bibleTexts[selectedLectionary.secondReading]}</p>
                      </div>
                    )}
                  </div>
                )}

                {/* Psalm */}
                {selectedLectionary.psalm && (
                  <div className="list-group-item px-0">
                    <h6 className="fw-bold text-muted small uppercase mb-1">Responsorial Psalm</h6>
                    <p className="mb-0 fs-5">{selectedLectionary.psalm}</p>
                    {bibleTexts[selectedLectionary.psalm] && (
                      <div className="mt-3 p-3 bg-success bg-opacity-10 border-start border-4 border-success rounded-end">
                        <p className="mb-0 small leading-relaxed text-dark">{bibleTexts[selectedLectionary.psalm]}</p>
                      </div>
                    )}
                  </div>
                )}

                {/* Gospel */}
                {selectedLectionary.gospel && (
                  <div className="list-group-item px-0 border-0">
                    <h6 className="fw-bold text-primary small uppercase mb-1">Holy Gospel</h6>
                    <p className="mb-0 fs-4 fw-bold">{selectedLectionary.gospel}</p>
                    {bibleTexts[selectedLectionary.gospel] && (
                      <div className="mt-3 p-3 bg-primary bg-opacity-10 border-start border-4 border-primary rounded-end">
                        <p className="mb-0 small leading-relaxed text-dark">{bibleTexts[selectedLectionary.gospel]}</p>
                      </div>
                    )}
                  </div>
                )}
              </div>
              <div className="mt-4 pt-3 border-top d-flex justify-content-between align-items-center">
                <div className="d-flex gap-2">
                  <span className="badge bg-dark">YEAR {selectedLectionary.cycle?.sunCycle}</span>
                  <span className="badge bg-secondary">CYCLE {selectedLectionary.cycle?.weekdayCycle}</span>
                </div>
                <div className="text-end">
                  <span className="text-muted small uppercase fw-bold ls-1">{selectedLectionary.identity?.rank}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* TRADING MODAL */}
      {selectedTrading && (
        <div className="modal-overlay" onClick={() => setSelectedTrading(null)}>
          <div className="modal-card shadow-lg p-0" onClick={e => e.stopPropagation()}>
            <div className="modal-header bg-dark text-white p-4">
              <div>
                <span className="badge bg-success mb-2">📈 HIGH-CONVICTION TRADING PLAN</span>
                <h2 className="m-0 fw-bold">{selectedTrading.title}</h2>
              </div>
              <button className="btn-close btn-close-white" onClick={() => setSelectedTrading(null)}></button>
            </div>
            <div className="modal-body p-4">
              <div className="row g-3 mb-4">
                <div className="col-md-4">
                  <div className="p-3 border rounded bg-white shadow-sm">
                    <h6 className="small fw-bold text-muted uppercase">1. Volume (Millions)</h6>
                    <ResponsiveContainer width="100%" height={80}>
                      <LineChart data={tradingChartData}>
                        <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#eee" />
                        <XAxis dataKey="displayDate" hide />
                        <YAxis hide domain={['auto', 'auto']} />
                        <Tooltip />
                        <Line type="monotone" dataKey="volume" stroke="#6f42c1" strokeWidth={2} dot={{ r: 3 }} />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                </div>
                <div className="col-md-4">
                  <div className="p-3 border rounded bg-white shadow-sm">
                    <h6 className="small fw-bold text-muted uppercase">2. CVD (Aggression)</h6>
                    <ResponsiveContainer width="100%" height={80}>
                      <LineChart data={tradingChartData}>
                        <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#eee" />
                        <XAxis dataKey="displayDate" hide />
                        <YAxis hide domain={['auto', 'auto']} />
                        <Tooltip />
                        <Line type="monotone" dataKey="cvd" stroke="#198754" strokeWidth={2} dot={{ r: 3 }} />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                </div>
                <div className="col-md-4">
                  <div className="p-3 border rounded bg-white shadow-sm">
                    <h6 className="small fw-bold text-muted uppercase">3. Spot CVD (Whales)</h6>
                    <ResponsiveContainer width="100%" height={80}>
                      <LineChart data={tradingChartData}>
                        <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#eee" />
                        <XAxis dataKey="displayDate" hide />
                        <YAxis hide domain={['auto', 'auto']} />
                        <Tooltip />
                        <Line type="monotone" dataKey="spotCvd" stroke="#dc3545" strokeWidth={2} dot={{ r: 3 }} />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                </div>
                <div className="col-md-4">
                  <div className="p-3 border rounded bg-white shadow-sm">
                    <h6 className="small fw-bold text-muted uppercase">4. Funding Rate</h6>
                    <ResponsiveContainer width="100%" height={80}>
                      <LineChart data={tradingChartData}>
                        <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#eee" />
                        <XAxis dataKey="displayDate" hide />
                        <YAxis hide domain={['auto', 'auto']} />
                        <Tooltip />
                        <Line type="monotone" dataKey="funding" stroke="#ffc107" strokeWidth={2} dot={{ r: 3 }} />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                </div>
                <div className="col-md-4">
                  <div className="p-3 border rounded bg-white shadow-sm">
                    <h6 className="small fw-bold text-muted uppercase">5. Open Interest (K)</h6>
                    <ResponsiveContainer width="100%" height={80}>
                      <LineChart data={tradingChartData}>
                        <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#eee" />
                        <XAxis dataKey="displayDate" hide />
                        <YAxis hide domain={['auto', 'auto']} />
                        <Tooltip />
                        <Line type="monotone" dataKey="oi" stroke="#0dcaf0" strokeWidth={2} dot={{ r: 3 }} />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                </div>
                <div className="col-md-4">
                  <div className="p-3 border rounded bg-white shadow-sm">
                    <h6 className="small fw-bold text-muted uppercase">6. Bid/Ask Delta</h6>
                    <ResponsiveContainer width="100%" height={80}>
                      <LineChart data={tradingChartData}>
                        <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#eee" />
                        <XAxis dataKey="displayDate" hide />
                        <YAxis hide domain={['auto', 'auto']} />
                        <Tooltip />
                        <Line type="monotone" dataKey="bidAsk" stroke="#fd7e14" strokeWidth={2} dot={{ r: 3 }} />
                      </LineChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              </div>
              <div className="markdown-content">
                <ReactMarkdown>{tradingContent}</ReactMarkdown>
              </div>
            </div>
          </div>
        </div>
      )}

      {selectedSaint && (
        <div className="modal-overlay" onClick={() => setSelectedSaint(null)}>
          <div className="modal-card shadow-lg p-0" style={{ maxWidth: '600px' }} onClick={e => e.stopPropagation()}>
            <div className="modal-header bg-danger text-white">
              <h3 className="m-0">🕊️ {selectedSaint.name}</h3>
              <button className="btn-close btn-close-white" onClick={() => setSelectedSaint(null)}></button>
            </div>
            <div className="modal-body p-0">
              <div className="saint-photo-container overflow-hidden">
                <img 
                  src={selectedSaint.image && !selectedSaint.image.includes('placeholder') ? selectedSaint.image : `/assets/generated/saint_2026_${String(new Date(selectedSaint.date).getMonth() + 1).padStart(2, '0')}_${String(new Date(selectedSaint.date).getDate()).padStart(2, '0')}.png`} 
                  className="img-fluid w-100 animated-saint-photo" 
                  alt={selectedSaint.name} 
                  onError={(e) => { e.target.src = '/assets/generated/saint_placeholder.png'; }}
                />
              </div>
              <div className="p-4">
                <div className="mb-3">
                  <span className="badge bg-danger me-2">{selectedSaint.type || 'MEMORIAL'}</span>
                  <span className="badge bg-primary">{selectedSaint.origin || 'Vatican'}</span>
                </div>
                <h5>Biographical Profile</h5>
                <div className="markdown-content mb-3">
                  <ReactMarkdown>
                    {selectedSaint.bio || `Today the Church celebrates the ${selectedSaint.type} of ${selectedSaint.name}.`}
                  </ReactMarkdown>
                </div>
                <hr />
                <p><strong>Patronage:</strong> {selectedSaint.patronage || 'Universal'}</p>
                <p><strong>Symbols:</strong> {selectedSaint.symbols || 'Cross'}</p>
                {selectedSaint.contentScript && (
                  <div className="mt-4 p-3 bg-warning bg-opacity-10 border border-warning rounded">
                    <h5 className="text-dark fw-bold">📖 {selectedSaint.contentScript.title}</h5>
                    <p className="text-muted small mb-2"><em>Source: {selectedSaint.contentScript.source}</em></p>
                    <p className="fw-bold mb-2">{selectedSaint.contentScript.summary}</p>
                    <div className="markdown-content small">
                      <ReactMarkdown>{selectedSaint.contentScript.script}</ReactMarkdown>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* STORY MODAL */}
      {selectedStory && (
        <div className="modal-overlay" onClick={() => setSelectedStory(null)}>
          <div className="modal-card shadow-lg p-0" onClick={e => e.stopPropagation()}>
            <div className="modal-header bg-dark text-white p-4">
              <div>
                <span className="badge bg-warning text-dark mb-2">2026 MASTER STORY FORGE</span>
                <h2 className="m-0 fw-bold">{selectedStory?.name}</h2>
                <p className="mb-0 opacity-75">Concept: A Modern/Solarpunk Reimagining</p>
              </div>
              <button className="btn-close btn-close-white" onClick={() => setSelectedStory(null)}></button>
            </div>
            <div className="modal-body p-4 bg-light">
              <div className="row g-4">
                <div className="col-md-8">
                  {/* STORY IMAGE */}
                  <div className="card border-0 shadow-sm mb-4 overflow-hidden">
                    <img 
                      src={selectedStory?.date ? `/assets/generated/story_2026_${String(selectedStory.date.getMonth() + 1).padStart(2, '0')}_${String(selectedStory.date.getDate()).padStart(2, '0')}.png` : ''} 
                      className="img-fluid w-100" 
                      alt={selectedStory?.name}
                      onError={(e) => e.target.style.display = 'none'}
                    />
                  </div>
                  <div className="card border-0 shadow-sm mb-4">
                    <div className="card-body">
                      <h5 className="fw-bold text-primary">📜 Narrative Specification</h5>
                      <div className="row mt-3">
                        <div className="col-6"><strong>Genre:</strong> {selectedStory?.manuscriptSpecs?.genre}</div>
                        <div className="col-6"><strong>Tone:</strong> {selectedStory?.manuscriptSpecs?.tone}</div>
                        <div className="col-6"><strong>Perspective:</strong> {selectedStory?.manuscriptSpecs?.perspective}</div>
                        <div className="col-6"><strong>Pacing:</strong> {selectedStory?.manuscriptSpecs?.pacing}</div>
                      </div>
                    </div>
                  </div>
                  <div className="card border-0 shadow-sm mb-4">
                    <div className="card-body">
                      <h5 className="fw-bold text-success">🌍 World-Building & Setting</h5>
                      <p className="mt-2 text-muted">{selectedStory?.worldBuilding?.setting}</p>
                      <strong>Key Themes:</strong> {selectedStory?.worldBuilding?.themes}
                    </div>
                  </div>
                  <div className="card border-0 shadow-sm mb-4">
                    <div className="card-body story-text-display">
                      <h5 className="fw-bold text-dark border-bottom pb-2 mb-3">📖 The Story Draft</h5>
                      <div className="markdown-content">
                        <ReactMarkdown>{storyContent}</ReactMarkdown>
                      </div>
                    </div>
                  </div>
                  <div className="card border-0 shadow-sm">
                    <div className="card-body">
                      <h5 className="fw-bold text-danger">🖋️ Production Beat Sheet</h5>
                      <div className="mt-2">
                        <p><strong>Act 1:</strong> The Latch (Symmetry Start)</p>
                        <p><strong>Act 2:</strong> The Progress (POV Weaving & Conflict)</p>
                        <p><strong>Act 3:</strong> The Sanderlanche (Plot Convergence)</p>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="col-md-4">
                  <div className="card border-0 bg-white shadow-sm p-3 mb-4">
                    <h6 className="fw-bold text-dark border-bottom pb-2">🎯 Writing Goals</h6>
                    <ul className="list-unstyled small">
                      <li className="mb-2"><strong>Daily:</strong> {selectedStory?.writingPlan?.dailyGoal}</li>
                      <li className="mb-2"><strong>Weekly:</strong> {selectedStory?.writingPlan?.weeklyGoal}</li>
                      <li className="mb-2"><strong>Target:</strong> {selectedStory?.manuscriptSpecs?.wordCountTarget}</li>
                    </ul>
                  </div>
                  <div className="card border-0 bg-info bg-opacity-10 shadow-sm p-3">
                    <h6 className="fw-bold text-info border-bottom pb-2">🧠 Revision Strategy</h6>
                    <p className="small mb-0 text-dark">{selectedStory?.writingPlan?.revisionPlan}</p>
                  </div>
                  <div className="card border-0 bg-success bg-opacity-10 shadow-sm p-3 mt-4">
                    <h6 className="fw-bold text-success border-bottom pb-2">📈 2026 Market Logic</h6>
                    <p className="small mb-0 text-dark"><strong>Optimal Length:</strong> {selectedStory?.marketLogic?.optimalWordCount}</p>
                    <p className="small mb-0 mt-2 text-muted italic">"{selectedStory?.marketLogic?.marketWhy}"</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* RECIPE MODAL */}
      {selectedRecipe && (
        <div className="modal-overlay" onClick={() => setSelectedRecipe(null)}>
          <div className="modal-card shadow-lg" onClick={e => e.stopPropagation()}>
            <div className="modal-header d-block">
              <div className="d-flex justify-content-between align-items-start">
                <div>
                  <span className="badge bg-dark mb-2">{selectedRecipe?.level}</span>
                  <h2 className="m-0 text-primary fw-bold">{selectedRecipe?.name}</h2>
                  <div className="mt-2 text-muted small">
                    <span className="me-3">⏱️ Prep: {selectedRecipe?.prepTime}</span>
                    <span className="me-3">🔥 Cook: {selectedRecipe?.cookTime}</span>
                    <span className="me-3">🍽️ {selectedRecipe?.servings}</span>
                  </div>
                </div>
                <button className="btn-close" onClick={() => setSelectedRecipe(null)}></button>
              </div>
            </div>
            <div className="modal-body">
              {recipeMarkdown ? (
                <div className="markdown-content p-3">
                  <ReactMarkdown>{recipeMarkdown}</ReactMarkdown>
                </div>
              ) : selectedRecipe?.ingredients ? (
                <div className="row g-4">
                  <div className="col-md-7">
                    {/* RECIPE IMAGE */}
                    <div className="card border-0 shadow-sm mb-4 overflow-hidden">
                      <img 
                        src={selectedRecipe?.date ? `/assets/generated/recipe_2026_${String(selectedRecipe.date.getMonth() + 1).padStart(2, '0')}_${String(selectedRecipe.date.getDate()).padStart(2, '0')}.png` : ''} 
                        className="img-fluid w-100" 
                        alt={selectedRecipe?.name}
                        onError={(e) => e.target.style.display = 'none'}
                      />
                    </div>
                    <h5 className="fw-bold text-dark mb-3">🛒 Ingredients (Authentic Ratios):</h5>
                    <div className="mb-3">
                      <h6 className="text-primary fw-bold small uppercase">Primary Protein</h6>
                      <ul className="list-group list-group-flush shadow-sm rounded border mb-3">
                        {selectedRecipe?.ingredients?.protein?.map((ing, i) => <li key={i} className="list-group-item py-2">{ing}</li>)}
                      </ul>
                    </div>
                    <div className="mb-3">
                      <h6 className="text-primary fw-bold small uppercase">Seasonal Base</h6>
                      <ul className="list-group list-group-flush shadow-sm rounded border mb-3">
                        {selectedRecipe?.ingredients?.base?.map((ing, i) => <li key={i} className="list-group-item py-2">{ing}</li>)}
                      </ul>
                    </div>
                    <h5 className="fw-bold text-dark mb-3">👨‍🍳 Chef's Protocol:</h5>
                    <div className="bg-white p-3 rounded border shadow-sm mb-4">
                      {selectedRecipe?.instructions?.map((step, i) => (
                        <div key={i} className="mb-3 border-bottom pb-2">
                          <span className="badge bg-primary mb-2">{step.step}</span>
                          <p className="mb-0 text-dark small">{step.text}</p>
                        </div>
                      ))}
                    </div>
                    <div className="p-3 bg-light rounded border-start border-4 border-warning">
                      <h6 className="fw-bold text-warning-emphasis">💡 Secret Hack:</h6>
                      <p className="mb-0 italic text-muted small">"{selectedRecipe?.chefSecret}"</p>
                    </div>
                  </div>
                  <div className="col-md-5">
                    <div className="card border-primary border-opacity-25 shadow-sm">
                      <div className="card-body">
                        <h5 className="card-title border-bottom pb-2 fw-bold text-center text-primary">⚖️ Nutrition</h5>
                        <div className="d-flex justify-content-between py-2 border-bottom"><span>Daily Calories:</span> <strong>{selectedRecipe?.nutrients?.calories} kkal</strong></div>
                        <div className="d-flex justify-content-between py-2 border-bottom"><span>Protein:</span> <strong>{selectedRecipe?.nutrients?.protein}</strong></div>
                        <div className="mt-4 p-3 bg-warning bg-opacity-10 rounded border border-warning border-opacity-25">
                          <h6 className="fw-bold mb-1 text-dark">💰 Price:</h6>
                          <p className="mb-0 fs-5">Cost per day: <strong>${selectedRecipe?.cost?.toFixed(2)}</strong></p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="text-center py-5">
                  <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                  <p className="mt-2">Synchronizing Culinary Protocol...</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {showShopList && (
        <div className="modal-overlay" onClick={() => setShowShopList(false)}>
          <div className="modal-card shadow-lg p-4" style={{ maxWidth: '600px' }} onClick={e => e.stopPropagation()}>
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h3 className="m-0"><span className="me-2">🛒</span>Organic Shopping List</h3>
              <button className="btn-close" onClick={() => setShowShopList(false)}></button>
            </div>
            <div className="table-responsive">
              <table className="table table-hover align-middle">
                <thead><tr><th>Item</th><th className="text-center">Qty</th><th className="text-end">Price</th></tr></thead>
                <tbody>
                  {dynamicShoppingList.map((item, i) => (
                    <tr key={i}><td>{item.item}</td><td className="text-center">{item.qty}</td><td className="text-end fw-bold">${item.price.toFixed(2)}</td></tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {showSitePlan && (
        <div className="modal-overlay" onClick={() => setShowSitePlan(false)}>
          <div className="modal-card shadow-lg p-0" style={{ maxWidth: '900px' }} onClick={e => e.stopPropagation()}>
            <div className="modal-header bg-success text-white">
              <h3 className="m-0">🏡 Kansas Prairie Homestead Site Plan</h3>
              <button className="btn-close btn-close-white" onClick={() => setShowSitePlan(false)}></button>
            </div>
            <div className="modal-body p-0">
              <img src="/assets/generated/homestead_site_plan.png" className="img-fluid w-100" alt="Homestead Site Plan" />
              <div className="p-4 bg-light">
                <h5>Layout Overview</h5>
                <p>Zone-based master plan for 6-acre self-sufficiency, featuring KS Homesteader Wheat, Nubian Goats, and integrated aquaculture.</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {showFrontierPlan && (
        <div className="modal-overlay" onClick={() => setShowFrontierPlan(false)}>
          <div className="modal-card shadow-lg p-0" style={{ maxWidth: '900px' }} onClick={e => e.stopPropagation()}>
            <div className="modal-header bg-dark text-white">
              <h3 className="m-0">🤠 Frontier Folly Studios Master Plan</h3>
              <button className="btn-close btn-close-white" onClick={() => setShowFrontierPlan(false)}></button>
            </div>
            <div className="modal-body p-0">
              <img src="/assets/generated/frontier_folly_site_plan.png" className="img-fluid w-100" alt="Frontier Folly Site Plan" />
              <div className="p-4 bg-light">
                <h5>The "Dual-Use" Concept</h5>
                <p><strong>Main Street (Zone A):</strong> Functional farm buildings (Livery/Barn, Saloon/Kitchen, Jail/Cellar) disguised as an 1880s western town.</p>
                <div className="card border-0 shadow-sm mb-3">
                  <div className="row g-0">
                    <div className="col-md-5">
                      <img src="/assets/generated/frontier_folly_castle_v2.png" className="img-fluid rounded-start h-100 object-fit-cover" alt="The Keep" />
                    </div>
                    <div className="col-md-7">
                      <div className="card-body">
                        <h6 className="card-title fw-bold">🏰 The Keep (Classical Design)</h6>
                        <p className="card-text small">A classical European-style castle adapted for the Kansas prairie using native Cottonwood Limestone. Features high crenellated curtain walls, twin circular towers, and a central keep, surrounded by a natural moat.</p>
                      </div>
                    </div>
                  </div>
                </div>
                <p><strong>The Keep (Zone B):</strong> A limestone castle residence serving as the B&B and primary dwelling.</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {showSoloGuide && (
        <div className="modal-overlay" onClick={() => setShowSoloGuide(false)}>
          <div className="modal-card shadow-lg p-0" style={{ maxWidth: '900px' }} onClick={e => e.stopPropagation()}>
            <div className="modal-header bg-primary text-white">
              <h3 className="m-0">🏗️ Solo Builder's Guide: The Slipform Keep</h3>
              <button className="btn-close btn-close-white" onClick={() => setShowSoloGuide(false)}></button>
            </div>
            <div className="modal-body p-4 bg-light">
              <div className="row g-4">
                <div className="col-md-6">
                  <h5 className="fw-bold text-primary">1. The Technique: Slipforming</h5>
                  <img src="/assets/generated/slipform_method.png" className="img-fluid rounded border shadow-sm mb-3" alt="Slipform Method" />
                  <p className="small">Use wooden forms to act as a 'human 3D printer'. Fill with 10lb rocks and concrete, slide forms up 18" daily. Perfect for solo work.</p>
                </div>
                <div className="col-md-6">
                  <h5 className="fw-bold text-success">2. Phase 1: The Gatehouse</h5>
                  <img src="/assets/generated/solo_keep_phase1.png" className="img-fluid rounded border shadow-sm mb-3" alt="Phase 1 Tower" />
                  <p className="small">Start with a single 16x16 tower. Move in once the first two floors are capped. Build the rest debt-free while living on-site.</p>
                </div>
              </div>
              <div className="mt-4 p-3 bg-white rounded border">
                <h6 className="fw-bold border-bottom pb-2">Why this works:</h6>
                <ul className="small">
                  <li><strong>Cost:</strong> $100k vs $1.2M (Materials only).</li>
                  <li><strong>Lifting:</strong> Only use stones you can carry by hand.</li>
                  <li><strong>Pace:</strong> Work at your own speed with no contractor deadlines.</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      )}

      {showCostAnalysis && (
        <div className="modal-overlay" onClick={() => setShowCostAnalysis(false)}>
          <div className="modal-card shadow-lg p-0" style={{ maxWidth: '800px' }} onClick={e => e.stopPropagation()}>
            <div className="modal-header bg-danger text-white p-4">
              <div>
                <span className="badge bg-white text-danger mb-2">💰 STRATEGIC COST AUDIT</span>
                <h2 className="m-0 fw-bold">Castle Cost Analysis (2026)</h2>
              </div>
              <button className="btn-close btn-close-white" onClick={() => setShowCostAnalysis(false)}></button>
            </div>
            <div className="modal-body p-4">
              <div className="markdown-content">
                <ReactMarkdown>{costAnalysisContent}</ReactMarkdown>
              </div>
            </div>
          </div>
        </div>
      )}

      <header className="mb-4 position-relative">
        <h1 className="text-center mb-4 display-4 fw-bold text-primary">Kalender Pintar 2026</h1>
        <button className="btn btn-dark shadow sidebar-toggle-floating d-none d-lg-flex" onClick={toggleSidebar}>{showSidebar ? '→' : '📈'}</button>
        <div className="card border-0 shadow-sm p-3 mb-4 bg-light">
          <div className="row g-3 align-items-center">
            <div className="col-xl-4 d-flex justify-content-between align-items-center">
              <button className="btn btn-primary month-nav-btn" onClick={prevMonth}>&lt;</button>
              <h2 className="m-0 fw-bold">{monthsIndo[currentDate.getMonth()]} {currentDate.getFullYear()}</h2>
              <button className="btn btn-primary month-nav-btn" onClick={nextMonth}>&gt;</button>
            </div>
            <div className="col-xl-8 text-center text-xl-end">
              <div className="dropdown d-inline-block">
                <button className="btn btn-outline-secondary dropdown-toggle px-4 py-2" type="button" id="filterDropdown" data-bs-toggle="dropdown" aria-expanded="false" data-bs-auto-close="outside">⚙️ Filter & Menu</button>
                <div className="dropdown-menu dropdown-menu-end p-3 shadow border-0" aria-labelledby="filterDropdown" style={{ minWidth: '250px' }}>
                  {Object.keys(filters).map(cat => (
                    <div key={cat} className="form-check form-switch mb-2">
                      <input className="form-check-input" type="checkbox" checked={filters[cat]} onChange={() => toggleFilter(cat)} id={`${cat}Filter`} />
                      <label className="form-check-label d-flex align-items-center" htmlFor={`${cat}Filter`}>
                        <span className={`legend-box bg-${cat}`}></span> {cat.toUpperCase()}
                      </label>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </header>

      <div className="row g-4">
        <div className={`${showSidebar ? 'col-lg-9' : 'col-lg-12'} transition-all`}>
          <div className="calendar-grid mb-4">
            {daysIndo.map(day => <div key={day} className="calendar-day-header">{day}</div>)}
            {days.map((dayObj, index) => {
              const allDayEvents = getEventsForDate(dayObj.date);
              const recipeEvent = allDayEvents.find(e => e.recipeDetails);
              const storyEvent = allDayEvents.find(e => e.storyDetails);
              const otherEvents = allDayEvents.filter(e => !e.recipeDetails && !e.storyDetails);
              
              const ds = dayObj.date.toISOString().split('T')[0];
              const w = dailyWeather.find(dw => dw.date === ds) || { high: '-', low: '-', condition: '' };
              const isToday = dayObj.date.toDateString() === simulatedToday.toDateString();
              
              return (
                <div 
                  key={index} 
                  ref={isToday ? todayRef : null}
                  className={`calendar-day ${!dayObj.isCurrentMonth ? 'other-month' : ''} ${isToday ? 'today-focus' : ''}`}
                >
                  <div className="d-flex justify-content-between align-items-center mb-1">
                    <div className="d-flex align-items-center gap-2">
                      {dayObj.isCurrentMonth && (
                        <div className="script-trigger clickable-event" onClick={() => { console.log("Script clicked for:", dayObj.date); setSelectedScript({ date: new Date(dayObj.date) }); }} title="Daily Script">
                          📜
                        </div>
                      )}
                      <span className="date-number">{dayObj.date.getDate()}</span>
                      {isToday && <span className="badge bg-danger today-badge">HARI INI</span>}
                    </div>
                    <div className="d-flex gap-1 align-items-center">
                      {storyEvent && dayObj.isCurrentMonth && (
                        <div className="recipe-icon-trigger clickable-event text-primary-emphasis" onClick={() => setSelectedStory(storyEvent.storyDetails)} title={storyEvent.storyDetails.name} style={{color: '#6f42c1'}}>
                          📖🖋️
                        </div>
                      )}
                      {recipeEvent && dayObj.isCurrentMonth && (
                        <div className="recipe-icon-trigger clickable-event" onClick={() => setSelectedRecipe(recipeEvent.recipeDetails)} title={recipeEvent.recipeDetails.name}>
                          🍳{getFlag(recipeEvent.recipeDetails.cuisine)}
                        </div>
                      )}
                      {dayObj.isCurrentMonth && <a href={getReadingLink(dayObj.date)} target="_blank" rel="noopener noreferrer" className="reading-link">📖</a>}
                    </div>
                  </div>
                  <div className="events-list">
                    {otherEvents.map((evt, idx) => (
                      <div key={idx} 
                        className={`event-pill ${evt.colorOverride ? 'liturgical-' + evt.colorOverride.toLowerCase() : 'bg-' + evt.category} clickable-event ${evt.isShopping ? 'shopping-highlight' : ''}`} 
                        onClick={() => { 
                        if (evt.isShopping) { setActiveShopDate(evt.shopDate); setShowShopList(true); } 
                        if (evt.saintDetails) { setSelectedSaint({ ...evt.saintDetails, date: dayObj.date }); }
                        if (evt.lectionaryDetails) { setSelectedLectionary(evt.lectionaryDetails); }
                        if (evt.tradingDetails) { setSelectedTrading({ ...evt.tradingDetails, date: new Date(dayObj.date) }); }
                      }}>
                        {evt.title}
                      </div>
                    ))}
                  </div>
                  <div className="weather-footer mt-auto" title={`High ${w.high}F / Low ${w.low}F`}>
                    <span className="weather-icon">{w.condition}</span>
                    <span className="weather-temps"><span className="text-danger">{w.high}F</span>/<span className="text-danger small">{fToC(w.high)}C</span> <span className="mx-1">|</span> <span className="text-primary">{w.low}F</span>/<span className="text-primary small">{fToC(w.low)}C</span></span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
        {showSidebar && (
          <div className="col-lg-3 sidebar-container animate-fade-in">
            <div className="card shadow-sm border-0 mb-4 sticky-sidebar">
              <div className="card-header bg-white fw-bold border-0 pt-3">📉 Global Info</div>
              <div className="card-body">
                <ResponsiveContainer width="100%" height={180}>
                  <LineChart data={chartData}><XAxis dataKey="day" hide /><YAxis hide /><Tooltip /><Line type="monotone" dataKey="High (°F)" stroke="#dc3545" dot={false} strokeWidth={3} /></LineChart>
                </ResponsiveContainer>
                <div className="mt-3">
                  <button className="btn btn-warning w-100 mb-2 fw-bold" onClick={() => { setActiveShopDate(null); setShowShopList(true); }}>🛒 View Current Shopping List</button>
                  <button className="btn btn-success w-100 mb-2 fw-bold" onClick={() => setShowSitePlan(true)}>🏡 View Homestead Site Plan</button>
                  <button className="btn btn-dark w-100 mb-2 fw-bold" onClick={() => setShowFrontierPlan(true)}>🤠 View Frontier Folly Plan</button>
                  <button className="btn btn-danger w-100 mb-2 fw-bold" onClick={() => setShowCostAnalysis(true)}>💰 Castle Cost Analysis</button>
                  <button className="btn btn-primary w-100 mb-2 fw-bold" onClick={() => window.open('/trading/Master_Protocol.md', '_blank')}>📈 Master Trading Protocol</button>
                  <button className="btn btn-outline-primary w-100 mb-2 fw-bold" onClick={() => setShowSoloGuide(true)}>🏗️ Solo Builder's Guide</button>
                  <div className="p-2 bg-light rounded small border">
                    <strong>Story & Meal System:</strong><br/>
                    366 Stories & Recipes.<br/>
                    Masters Technique Integrated.
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
    </>
  );
};

export default App;